/**
 * Product Template Component
 * ------------------------------------------------------------------------------
 * Custom Web Component that handles product display and interaction
 *
 * Features:
 * - Fetches product data via GraphQL from Shopify Storefront API
 * - Renders product options (color, size, etc.) dynamically
 * - Handles variant selection and availability checking
 * - Manages add to cart functionality with gift card support
 * - Updates pricing and inventory in real-time
 */

import { setSoldOutLabel } from './card-a11y.js';

class ProductTemplate extends HTMLElement {
  // ============================================================================
  // CONSTANTS - Centralized configuration values
  // ============================================================================

  /** Product option types used throughout the component */
  static OPTION_TYPES = {
    COLOR: 'color',
    SIZE: 'size',
    TITLE: 'title',
  };

  /** CSS class names for consistent styling */
  static CSS_CLASSES = {
    HIDDEN: 'hidden',
    ACTIVE: 'active',
    DISABLED: 'disabled',
    PRODUCT_CTA: 'product__cta',
    SIZE_OPTION: 'product__size-option',
    OPTION_WRAP: 'product__options-wrap',
    OPTION_COLOR: 'product__option-color',
    OPTION_SIZE: 'product__option-size',
    SIZE_ERROR: 'size-error',
    KLAVIYO_BIS_DISABLED: 'klaviyo-bis-disabled',
  };

  /** DOM selectors for element queries */
  static SELECTORS = {
    PRODUCT_CTA: '.product__cta',
    SIZE_ERROR: '.size-error',
    OPTION_COLOR_INPUT: '.product__option-color input:checked',
    OPTIONS_WRAP_INPUT: '.product__options-wrap input:checked',
    SIZE_OPTION: '.product__size-option',
  };

  /** Default text values for UI elements */
  static DEFAULT_TEXT = {
    ADD_TO_CART: 'add to cart',
    SELECT_SIZE: 'select size',
    SELECT_COLOR: 'Select Color',
    SOLD_OUT: 'sold out',
  };

  // ============================================================================
  // CONSTRUCTOR - Initialize component state and configuration
  // ============================================================================

  constructor() {
    super();

    // API Configuration - Shopify Storefront API settings
    this.accessToken = '8bcb2ea2ba9acd5b735e7abe4cbca7d0';
    this.graphUrl =
      'https://liquiddeath.myshopify.com/api/2025-07/graphql.json';

    // DOM Elements - Cache frequently accessed elements
    this.product = this.dataset.handle; // Product handle from data attribute
    this.options = this.querySelector('#product-options'); // Container for product options

    // Product State - Track current product data and selections
    this.productData = null; // Will be populated after GraphQL fetch
    this.selectedColor = null; // Currently selected color variant
    this.swatchLabelOverride = window.swatchLabelOverride; // Custom label override

    // Configuration - Component behavior settings
    this.context = this.getAttribute('data-market').toUpperCase(); // Market context (US, EU, etc.)
    this.currency = this.getAttribute('data-currency'); // Currency symbol
    this.enableGiftCardRecipient =
      this.getAttribute('data-enable-gift-card-recipient') === 'true'; // Gift card feature toggle

    // DOM Element Cache - Store frequently accessed elements for performance
    this.cachedElements = {};
  }
  // ============================================================================
  // LIFECYCLE METHODS - Component initialization and setup
  // ============================================================================

  /**
   * Called when the component is connected to the DOM
   * Initializes the product template by fetching data and setting up the UI
   */
  async connectedCallback() {
    if (this.querySelector("[data-product-popup]")) return;
    try {
      // Check if we have Liquid-rendered initial content
      this.hasLiquidContent = this.options?.querySelector('[data-initial-render="true"]') !== null;

      // Step 1: Fetch product data from Shopify GraphQL API
      await this.fetchProductData();

      // Step 2: Initialize product state (selected color, etc.)
      this.initializeProductState();

      // Step 3: Render or enhance the product options
      if (this.hasLiquidContent) {
        // Liquid already rendered options - just enhance with JS functionality
        this.enhanceLiquidOptions();
      } else {
        // No Liquid content - render via JS (fallback)
        this.renderOptions();
      }

      // Step 4: Set up event listeners for user interactions
      this.setupEventListeners();

      // Step 5: Set initial availability state (disable sold out variants)
      // Skip for Liquid content - availability already handled in Liquid/enhanceLiquidOptions
      if (!this.hasLiquidContent) {
        this.checkAvailability();
      }
    } catch (err) {
      console.error('Error initializing product template:', err);
      this.handleInitializationError(err);
    }
  }

  /**
   * Enhances Liquid-rendered product options with JS functionality
   * Called when initial options are already rendered via Liquid
   * Liquid already handles availability - we just need to cache elements and set up listeners
   */
  enhanceLiquidOptions() {
    // Cache DOM elements from Liquid-rendered content
    this.cacheDOMElements();

    // Check if Liquid rendered the swatches - if not, render them via JS
    const hasSwatches = this.options?.querySelector('.swatch');
    if (!hasSwatches && window.relatedSwatches?.length > 0) {
      // Liquid didn't render swatches, add them via JS
      const swatchHTML = this.buildRelatedSwatchesHTML();
      if (swatchHTML) {
        const initialContent = this.options?.querySelector('.product-options-initial');
        if (initialContent) {
          initialContent.insertAdjacentHTML('afterbegin', swatchHTML);
        }
      }
    }

    // Set up gift card listener if needed
    this.setupGiftCardEventListener();

    // Set up BIS (Back in Stock) click handlers for unavailable variants
    this.setupBisClickHandlers();

    // Liquid pre-checks the first available option but renders the CTA inactive.
    // Activate it now so the pre-selected variant is purchasable without first
    // re-selecting an option (otherwise add-to-cart shows "select size").
    this.updateProductAvailability();
  }

  /**
   * Sets up Klaviyo BIS click handlers for unavailable variant size options
   * Used by Liquid-rendered content where disabled classes are already set
   */
  setupBisClickHandlers() {
    const unavailableVariants = this.getUnavailableVariants();
    if (!unavailableVariants.length) return;

    const sizeOptions = this.querySelectorAll(
      ProductTemplate.SELECTORS.SIZE_OPTION
    );

    unavailableVariants.forEach((variant) => {
      // Get the size part from variant title (format: "Color / Size" or just "Size")
      const variantParts = variant.node.title.split(' / ');
      const variantSize = variantParts[variantParts.length - 1].trim();

      sizeOptions.forEach((sizeOption) => {
        const sizeName = sizeOption.getAttribute('data-name');
        // Use exact match instead of includes to avoid "L" matching "XL"
        if (sizeName === variantSize) {
          // Ensure the option is clickable even when disabled
          sizeOption.classList.add('clickable-disabled');
          setSoldOutLabel(sizeOption, true);
          sizeOption.addEventListener('click', (e) => {
            e.preventDefault();
            this.handleUnavailableVariantClick(variant);
          });
        }
      });
    });
  }

  /**
   * Fetches product data from Shopify Storefront GraphQL API
   * Populates this.productData with complete product information
   */
  async fetchProductData() {
    const response = await fetch(this.graphUrl, {
      method: 'POST',
      headers: {
        'X-Shopify-Storefront-Access-Token': this.accessToken,
        'Content-Type': 'application/graphql',
      },
      body: this.buildProductQuery(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    this.productData = result.data?.product;

    if (!this.productData) {
      throw new Error('Product data not found');
    }
  }

  /**
   * Initializes product state after data is fetched
   * Sets default selected color and prepares component for rendering
   */
  initializeProductState() {
    this.selectedColor = this.getColorOption()?.values[0] || null;
  }

  // ============================================================================
  // UTILITY METHODS - Helper functions for data access and DOM management
  // ============================================================================

  /**
   * Gets the color option from product data
   * @returns {Object|null} Color option object or null if not found
   */
  getColorOption() {
    return this.productData?.options.find(
      (option) =>
        option.name.toLowerCase() === ProductTemplate.OPTION_TYPES.COLOR
    );
  }

  /**
   * Gets the size option from product data
   * @returns {Object|null} Size option object or null if not found
   */
  getSizeOption() {
    return this.productData?.options.find(
      (option) =>
        option.name.toLowerCase() === ProductTemplate.OPTION_TYPES.SIZE
    );
  }

  /**
   * Caches frequently accessed DOM elements for performance
   * Called after options are rendered to ensure elements exist
   */
  cacheDOMElements() {
    this.cachedElements = {
      productCTA: this.querySelector(ProductTemplate.SELECTORS.PRODUCT_CTA),
      sizeError: this.querySelector(ProductTemplate.SELECTORS.SIZE_ERROR),
    };
  }

  /**
   * Handles initialization errors
   * @param {Error} error - The error that occurred during initialization
   */
  handleInitializationError(error) {
    // Could add user-facing error handling here
    console.error('Failed to initialize product template:', error);
  }

  // ============================================================================
  // GRAPHQL METHODS - Product data fetching and processing
  // ============================================================================

  /**
   * Builds the GraphQL query string for fetching product data
   * Includes product info, variants, options, and metafields
   * @returns {string} GraphQL query string
   */
  buildProductQuery() {
    return `query @inContext(country: ${this.context}) {
              product(handle: "${this.product}") {
                id
                isGiftCard
                title
                handle
                featuredImage {
                  url
                  id
                }
                metafields(identifiers: [
                  {namespace: "custom", key: "swatch"}, 
                  {namespace: "pdp", key: "amazon_link"}, 
                  {namespace: "pdp", key: "amazon_button_text"}, 
                  {namespace: "pdp", key: "announcement_tag"}, 
                  {namespace: "custom", key: "related_swatches"}
                ]) {
                  value
                }
                variants(first: 100) {
                  edges {
                    node {
                      id
                      price {
                        amount
                      }
                      compareAtPrice {
                        amount
                      }
                      title
                      availableForSale
                      quantityAvailable
                      image {
                        url
                        id
                      }
                    }
                  }
                }
                options {
                  name
                  values
                }
              }
            }`;
  }

  /**
   * Decodes Shopify's Global ID to get the numeric variant ID
   * @param {string} id - Shopify Global ID (e.g., "gid://shopify/ProductVariant/123")
   * @returns {number} Numeric variant ID
   */
  decodeId(id) {
    const shopifyId = id.split('/').pop();
    return parseInt(shopifyId);
  }

  // ============================================================================
  // RENDERING METHODS - HTML generation and DOM manipulation
  // ============================================================================

  /**
   * Main rendering method - builds and inserts product options HTML
   * Called after product data is fetched and state is initialized
   */
  renderOptions() {
    const html = this.buildOptionsHTML();
    this.options.insertAdjacentHTML('beforeend', html);
    this.cacheDOMElements(); // Cache elements after they're rendered
    this.setupGiftCardEventListener();
  }

  /**
   * Builds the complete HTML structure for product options
   * Orchestrates the building of all option types and UI elements
   * @returns {string} Complete HTML string for product options
   */
  buildOptionsHTML() {
    let html = '<div class="flex flex-col">';

    // Build option HTML for each product option (color, size, etc.)
    this.productData.options.forEach((option) => {
      if (option.name !== ProductTemplate.OPTION_TYPES.TITLE) {
        // Skip color option if related swatches exist (they will be rendered separately)
        const isColorOption =
          option.name.toLowerCase() === ProductTemplate.OPTION_TYPES.COLOR;
        const hasRelatedSwatches = window.relatedSwatches?.length > 0;
        if (isColorOption && hasRelatedSwatches) {
          return; // Skip this option
        }
        html += this.buildOptionHTML(option);
      }
    });

    // Handle related swatches if they exist (for color variations)
    html += this.buildRelatedSwatchesHTML();

    // Build action buttons (add to cart)
    html += this.buildActionButtonHTML();

    // Build gift card form if applicable
    html += this.buildGiftCardHTML();
    html += '</div>';

    return html;
  }

  /**
   * Builds HTML for a single product option (color, size, etc.)
   * @param {Object} option - Product option object with name and values
   * @returns {string} HTML string for the option
   */
  buildOptionHTML(option) {
    const optionType = option.name.toLowerCase();
    const isColor = optionType === ProductTemplate.OPTION_TYPES.COLOR;
    const isSize = optionType === ProductTemplate.OPTION_TYPES.SIZE;
    const hasSingleValue = option.values.length === 1;

    const cssClass = this.getOptionCSSClass(option);
    const shouldHide = this.shouldHideOption(option, hasSingleValue);

    let html = `<div class="${cssClass}${shouldHide ? ' !hidden' : ''}">`;
    html += this.buildOptionTitle(option, hasSingleValue);
    html += `<div class="product__options-wrap items-center flex flex-wrap mt-4${
      hasSingleValue ? ' mt-0' : ''
    }">`;

    // Build different option types based on whether it's color or size
    if (isColor) {
      html += this.buildColorOptions(option);
    } else {
      html += this.buildSizeOptions(option);
    }

    html += '</div></div>';

    // Add "Can't find your size?" link for size options if there are unavailable variants
    if (
      !isColor &&
      option.name.toLowerCase() === ProductTemplate.OPTION_TYPES.SIZE
    ) {
      const hasUnavailableVariants = this.productData?.variants?.edges?.some(
        (variant) => !variant.node.availableForSale
      );
      if (hasUnavailableVariants) {
        html += this.buildCantFindSizeLinkHTML();
      }
    }

    return html;
  }

  getOptionCSSClass(option) {
    const optionType = option.name.toLowerCase();
    if (
      optionType !== ProductTemplate.OPTION_TYPES.COLOR &&
      optionType !== ProductTemplate.OPTION_TYPES.SIZE
    ) {
      return ProductTemplate.CSS_CLASSES.OPTION_SIZE;
    }
    return `product__option-${optionType}`;
  }

  shouldHideOption(option, hasSingleValue) {
    const isColor =
      option.name.toLowerCase() === ProductTemplate.OPTION_TYPES.COLOR;
    // Show color options by default unless explicitly disabled
    // Only hide if showSwatches is explicitly false OR if related swatches exist
    const shouldHideColor =
      isColor &&
      (window.showSwatches === false || window.relatedSwatches?.length > 0);
    return hasSingleValue || shouldHideColor;
  }

  buildOptionTitle(option, hasSingleValue) {
    const optionType = option.name.toLowerCase();
    const showSelect =
      optionType === ProductTemplate.OPTION_TYPES.COLOR ||
      optionType === ProductTemplate.OPTION_TYPES.SIZE;
    const selectedValue = this.getSelectedValueForOption(option);
    const hiddenClass = hasSingleValue ? ' !hidden' : '';

    return `<div class="product__option-title text-dark${hiddenClass}" data-name="${
      option.name
    }">
              ${showSelect ? 'Select ' : ''}${
                option.name
              }: <span>${selectedValue}</span>
            </div>`;
  }

  getSelectedValueForOption(option) {
    if (option.name.toLowerCase() === ProductTemplate.OPTION_TYPES.COLOR) {
      return this.selectedColor || this.getColorOption()?.values[0] || '';
    }
    return '';
  }

  buildSizeOptions(option) {
    let html = '';

    option.values.forEach((size) => {
      const variant = this.findVariantByTitle(size);
      const inventoryQuantity = variant?.node.quantityAvailable || 0;
      const sizeId = size.replaceAll(' ', '-');
      const isChecked = option.values.length === 1;
      const hiddenClass = option.values.length === 1 ? ' hidden' : '';

      html += `<input type="radio" name="option-${
        option.name
      }" id="option-${sizeId}" 
                data-name="${option.name}" value="${size}" class="sr-only" 
                ${
                  isChecked ? 'checked' : ''
                } data-inventory="${inventoryQuantity}" />
               <label for="option-${sizeId}" 
                class="${hiddenClass} product__size-option text-base mb-2 flex text-dark 
                border border-solid border-dark p-1 min-w-[52px] m-[3px] h-10 
                items-center justify-center cursor-pointer" data-name="${size}">
                 <span class="p-1 bg-white z-10">${size}</span>
               </label>`;
    });

    const errorText =
      option.name.toLowerCase() !== ProductTemplate.OPTION_TYPES.SIZE
        ? option.name
        : ProductTemplate.DEFAULT_TEXT.SELECT_SIZE;

    html += `<div class="size-error hidden w-full mt-1">Please ${errorText}</div>`;

    return html;
  }

  /**
   * Builds HTML for the "Can't find your size?" link
   * @returns {string} HTML string for the cant find size link
   */
  buildCantFindSizeLinkHTML() {
    return `
      <div class="cant-find-size-link mb-4">
        <a href="#" class="text-base text-[#8a6d35] underline hover:no-underline cant-find-size-trigger cursor-pointer">
          Can't find your size?
        </a>
      </div>
    `;
  }

  buildColorOptions(option) {
    let html = '';
    const selectedColor =
      this.selectedColor || this.getColorOption()?.values[0];

    option.values.forEach((color) => {
      const colorValue = this.normalizeColorValue(color);
      const variant = this.findVariantByTitle(color);
      const inventoryQuantity = variant?.node.quantityAvailable || 0;
      const isChecked = color === selectedColor;

      html += `<input type="radio" name="option-${option.name}" 
                ${isChecked ? 'checked' : ''} id="option-${colorValue}" 
                data-name="${option.name}" value="${color}" class="sr-only" 
                data-inventory="${inventoryQuantity}" />
               <label for="option-${colorValue}" class="swatch ${colorValue} h-5 w-5 mr-4" 
                data-product="${this.productData.title}" data-name="${
                  option.name
                }" 
                style="background: ${colorValue}"><span class="sr-only">${color}</span></label>`;
    });

    return html;
  }

  normalizeColorValue(color) {
    return color.toLowerCase().replaceAll(' ', '-').replaceAll('/', '-');
  }

  findVariantByTitle(searchText) {
    return this.productData.variants.edges.find((variant) =>
      variant.node.title.toLowerCase().includes(searchText.toLowerCase())
    );
  }

  buildRelatedSwatchesHTML() {
    if (!window.relatedSwatches?.length) {
      return '';
    }

    const swatches = [
      window.mainSwatch,
      ...window.relatedSwatches.map((color) => color.swatch),
    ].sort();
    const label =
      this.swatchLabelOverride || ProductTemplate.DEFAULT_TEXT.SELECT_COLOR;

    const titleId = `ColorTitle-${this.product}`;

    // Swatches link to sibling color products, so they are a labelled list of links, not radios
    let html = `<div class="product__option-title text-dark" id="${titleId}">${label}: ${window.mainSwatch}</div>
                <ul class="items-center flex flex-wrap mt-4" role="list" aria-labelledby="${titleId}">`;

    swatches.forEach((swatch) => {
      const colorValue = this.normalizeColorValue(swatch);
      const isMainSwatch = swatch === window.mainSwatch;
      const href = isMainSwatch
        ? window.location.pathname
        : window.relatedSwatches.find((color) => color.swatch === swatch)?.link;

      html += `<li class="flex">
                 <a href="${href}" ${isMainSwatch ? 'aria-current="true"' : ''}
                    class="swatch ${colorValue} h-5 w-5 mr-4 block"><span class="sr-only">${swatch}</span></a>
               </li>`;
    });

    html += '</ul>';
    return html;
  }

  buildActionButtonHTML() {
    const buttonText = this.getButtonText();
    const hasSizeOptions = this.getSizeOption();
    const buttonClasses = `product__cta font-bold button-white block uppercase text-base w-full ${
      hasSizeOptions ? '' : ProductTemplate.CSS_CLASSES.ACTIVE
    }`;

    return `<div class="flex mt-6">
              <div class="animated-button-block-hero w-full">
                <button class="${buttonClasses}">
                  ${buttonText}
                </button>
              </div>
            </div>`;
  }

  getButtonText() {
    const metafieldValue = this.productData.metafields?.[2]?.value;
    return metafieldValue || ProductTemplate.DEFAULT_TEXT.ADD_TO_CART;
  }

  buildGiftCardHTML() {
    if (!this.productData.isGiftCard || !this.enableGiftCardRecipient) {
      return '</div></div>';
    }

    return `
        <div id="gift-card-recipient-block" class="mt-4">
          <label>
            <input type="checkbox" id="send-as-gift-checkbox" />
            Send as a gift
          </label>
          <div id="gift-card-recipient-form" style="display:none; margin-top:1em;">
          <input type="email" id="recipient-email" placeholder="Recipient email (required)" 
                 required class="block mb-2 w-full border p-2" />
          <input type="text" id="recipient-name" placeholder="Recipient name (optional)" 
                 class="block mb-2 w-full border p-2" />
          <input type="text" id="gift-message" placeholder="Message (optional)" 
                 class="block mb-2 w-full border p-2" />
          </div>
        </div>
      </div></div>`;
  }

  setupGiftCardEventListener() {
    const checkbox = document.getElementById('send-as-gift-checkbox');
    const form = document.getElementById('gift-card-recipient-form');

    if (checkbox && form) {
      checkbox.addEventListener('change', (e) => {
        form.style.display = e.target.checked ? 'block' : 'none';
      });
    }
  }

  // ============================================================================
  // EVENT HANDLING - User interaction management
  // ============================================================================

  /**
   * Sets up all event listeners for user interactions
   * Called during component initialization
   */
  setupEventListeners() {
    this.setupVariantChangeListener();
    this.setupAddToCartListener();
    this.setupAvailabilityCheckListener();
    this.setupCantFindSizeListener();
    // Note: klaviyo-bis-trigger is handled by Klaviyo's native BIS functionality
    // configured in theme.liquid with product_page_class: 'button-white'
  }

  /**
   * Sets up listener for variant option changes (color, size, etc.)
   * Triggers when user selects different option values
   */
  setupVariantChangeListener() {
    this.addEventListener('change', (e) => {
      if (!e.target.matches('input[type=radio][name^="option-"]')) {
        return;
      }

      this.handleVariantChange(e.target);
    });
  }

  /**
   * Handles when a variant option is changed by the user
   * Updates pricing, availability, and UI elements accordingly
   * @param {HTMLElement} target - The input element that was changed
   */
  handleVariantChange(target) {
    const optionName = target.getAttribute('data-name');
    const optionValue = target.value;

    // Update price display based on selected variant
    this.updatePriceDisplay(optionValue);

    // Update the option title to show selected value
    this.updateOptionTitle(optionName, optionValue);

    // Re-check availability for the new selection
    // Skip for Liquid content - availability is already set correctly
    if (!this.hasLiquidContent) {
      this.checkAvailability();
    } else {
      // For Liquid content, just update the CTA availability
      this.updateProductAvailability();
    }

    // Handle special case for color changes (update product images)
    if (optionName && optionName.toLowerCase() === ProductTemplate.OPTION_TYPES.COLOR) {
      this.handleColorChange(optionValue);
    }
  }

  updatePriceDisplay(optionValue) {
    const variant = this.findVariantByTitle(optionValue);
    if (!variant) return;

    const priceElement = document.getElementById('price__main');
    const comparePriceElement = document.getElementById('compare__price');
    const variantPrice = Number(variant.node.price.amount);
    let hasComparePrice = false;

    // Check if this product is excluded from promo
    const productHandle = this.getAttribute('data-handle');
    const isExcluded =
      window.promoExcludedProducts?.includes(productHandle) || false;

    // Apply promotional pricing if enabled and not excluded
    if (
      window.showPromoOriginalPrice &&
      window.promoDiscountPercentage > 0 &&
      !isExcluded
    ) {
      // Calculate sale price (original price with discount applied)
      const discountMultiplier = (100 - window.promoDiscountPercentage) / 100;
      const calculatedSalePrice = variantPrice * discountMultiplier;

      if (priceElement) {
        priceElement.textContent =
          this.currency + calculatedSalePrice.toFixed(2);
      }

      if (comparePriceElement) {
        hasComparePrice = true;
        // If compare_at_price exists, use it; otherwise show original price as slashed
        if (variant.node.compareAtPrice) {
          comparePriceElement.textContent =
            this.currency +
            Number(variant.node.compareAtPrice.amount).toFixed(2);
          comparePriceElement.removeAttribute('data-promo-calculated');
        } else {
          // Show original price as slashed
          comparePriceElement.textContent =
            this.currency + variantPrice.toFixed(2);
          comparePriceElement.setAttribute('data-promo-calculated', 'true');
        }
      }
    }
    // No promo - show regular pricing
    else {
      if (priceElement) {
        priceElement.textContent = this.currency + variantPrice.toFixed(2);
      }

      if (comparePriceElement) {
        // Show compare_at_price if it exists
        if (variant.node.compareAtPrice) {
          comparePriceElement.textContent =
            this.currency +
            Number(variant.node.compareAtPrice.amount).toFixed(2);
          hasComparePrice = true;
        } else {
          comparePriceElement.textContent = '';
        }
        comparePriceElement.removeAttribute('data-promo-calculated');
      }
    }

    this.updatePriceLabels(hasComparePrice);
  }

  /**
   * Shows the visually hidden "Sale price" / "Regular price" labels only when a
   * strikethrough price is displayed, so screen readers can tell the two apart
   * @param {boolean} hasComparePrice - Whether a strikethrough price is shown
   */
  updatePriceLabels(hasComparePrice) {
    document
      .querySelectorAll('[data-price-label], [data-compare-wrap]')
      .forEach((el) => {
        el.classList.toggle(ProductTemplate.CSS_CLASSES.HIDDEN, !hasComparePrice);
      });
  }

  updateOptionTitle(optionName, optionValue) {
    const titleElement = this.querySelector(
      `.product__option-title[data-name="${optionName}"] span`
    );
    if (titleElement) {
      titleElement.textContent = optionValue;
    }
  }

  handleColorChange(colorValue) {
    const variant = this.findVariantByTitle(colorValue);
    if (!variant?.node.image) return;

    const changeSliderEvent = new CustomEvent('changeSlider', {
      bubbles: true,
      cancelable: true,
      composed: true,
      detail: {
        media_id: variant.node.image.url,
      },
    });

    document.dispatchEvent(changeSliderEvent);
  }

  decodeId(id) {
    const shopifyId = id.split('/').pop();
    return parseInt(shopifyId);
  }

  setupAvailabilityCheckListener() {
    const productCTA = this.cachedElements.productCTA;
    if (productCTA) {
      productCTA.addEventListener('click', () => {
        if (
          !productCTA.classList.contains(ProductTemplate.CSS_CLASSES.ACTIVE)
        ) {
          this.showSizeError();
        }
      });
    }
  }

  /**
   * Sets up listener for the "Can't find your size?" link
   * Opens a general Klaviyo BIS modal when clicked
   */
  setupCantFindSizeListener() {
    this.addEventListener('click', (e) => {
      if (e.target.classList.contains('cant-find-size-trigger')) {
        e.preventDefault();
        this.openGeneralKlaviyoBISModal();
      }
    });
  }

  // ============================================================================
  // AVAILABILITY MANAGEMENT - Inventory and variant availability checking
  // ============================================================================

  /**
   * Main availability checking method
   * Called on initialization and when variant selections change
   * Updates disabled states and button availability
   */
  checkAvailability() {
    const productCTA = this.cachedElements.productCTA;
    if (productCTA?.classList.contains(ProductTemplate.CSS_CLASSES.ACTIVE)) {
      this.hideSizeError();
    }

    this.updateDisabledSizeOptions();
    this.updateProductAvailability();
  }

  updateDisabledSizeOptions() {
    const sizeOptions = this.querySelectorAll(
      ProductTemplate.SELECTORS.SIZE_OPTION
    );
    sizeOptions.forEach((option) => {
      option.classList.remove(ProductTemplate.CSS_CLASSES.DISABLED);
      option.classList.remove('clickable-disabled');
      setSoldOutLabel(option, false);
      // Remove any existing click handlers
      option.removeEventListener('click', this.handleUnavailableVariantClick);
    });

    const selectedColor = this.getSelectedColorValue();
    if (!selectedColor) {
      // If no color is selected, check all variants regardless of color
      const allUnavailableVariants = this.getUnavailableVariants();
      allUnavailableVariants.forEach((variant) => {
        // Find the size option by matching the variant title
        const sizeOptions = this.querySelectorAll(
          ProductTemplate.SELECTORS.SIZE_OPTION
        );
        sizeOptions.forEach((sizeOption) => {
          const sizeName = sizeOption.getAttribute('data-name');
          if (variant.node.title.includes(sizeName)) {
            sizeOption.classList.add(ProductTemplate.CSS_CLASSES.DISABLED);
            sizeOption.classList.add('clickable-disabled');
            setSoldOutLabel(sizeOption, true);
            sizeOption.addEventListener('click', (e) => {
              e.preventDefault();
              this.handleUnavailableVariantClick(variant);
            });
          }
        });
      });
      return;
    }

    const unavailableVariants =
      this.getUnavailableVariantsForColor(selectedColor);

    unavailableVariants.forEach((variant) => {
      const sizeOption = this.getSizeOptionFromVariant(variant, selectedColor);
      if (sizeOption) {
        sizeOption.classList.add(ProductTemplate.CSS_CLASSES.DISABLED);
        sizeOption.classList.add('clickable-disabled'); // Override pointer-events: none
        setSoldOutLabel(sizeOption, true);
        // Add click handler for unavailable variants
        sizeOption.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleUnavailableVariantClick(variant);
        });
      }
    });
  }

  getSelectedColorValue() {
    const colorInput = this.querySelector('input[name="option-Color"]:checked');
    return colorInput?.value;
  }

  getUnavailableVariantsForColor(colorValue) {
    return this.productData.variants.edges.filter(
      (variant) =>
        variant.node.title.includes(colorValue) &&
        !variant.node.availableForSale
    );
  }

  getSizeOptionFromVariant(variant, colorValue) {
    const sizeValue = variant.node.title
      .replace(colorValue, '')
      .replace('/', '')
      .trim();

    return this.querySelector(
      `.product__size-option[data-name="${sizeValue}"]`
    );
  }

  updateProductAvailability() {
    const selectedVariant = this.getSelectedVariant();
    const productCTA = this.cachedElements.productCTA;

    if (!selectedVariant?.node.availableForSale) {
      this.setProductUnavailable(productCTA);
    } else {
      this.setProductAvailable(productCTA);
    }
  }

  getSelectedVariant() {
    let selectedVariants = this.productData.variants.edges;

    if (selectedVariants.length > 1) {
      const checkedInputs = this.querySelectorAll(
        'input[type="radio"]:checked'
      );

      // Build a map of option values by finding the corresponding option index
      checkedInputs.forEach((input) => {
        const inputValue = input.value;

        // Find which option index this value belongs to
        this.productData.options.forEach((option, optionIndex) => {
          if (option.values.includes(inputValue)) {
            // Filter variants where this option position matches the input value
            selectedVariants = selectedVariants.filter(
              (variant) =>
                variant.node.title.split(' / ')[optionIndex]?.trim() ===
                inputValue
            );
          }
        });
      });
    }

    return selectedVariants[0];
  }

  setProductUnavailable(productCTA) {
    if (!productCTA) return;

    const hasSizeOptions = this.querySelectorAll(
      '.product__option-size'
    ).length;

    if (hasSizeOptions) {
      const allUnavailable = this.productData.variants.edges.every(
        (variant) => !variant.node.availableForSale
      );

      if (allUnavailable) {
        productCTA.textContent = ProductTemplate.DEFAULT_TEXT.SOLD_OUT;
      } else {
        // Check if the selected variant is sold out
        const selectedVariant = this.getSelectedVariant();
        if (!selectedVariant?.node.availableForSale) {
          productCTA.textContent = ProductTemplate.DEFAULT_TEXT.SOLD_OUT;
        }
      }
    } else {
      productCTA.textContent = ProductTemplate.DEFAULT_TEXT.SOLD_OUT;
    }

    productCTA.classList.remove(ProductTemplate.CSS_CLASSES.ACTIVE);
  }

  setProductAvailable(productCTA) {
    if (!productCTA) return;

    const hasSizeOptions = this.querySelectorAll(
      '.product__option-size'
    ).length;

    if (hasSizeOptions) {
      // Only activate if a size is selected
      if (this.querySelectorAll('.product__option-size input:checked').length) {
        productCTA.textContent = ProductTemplate.DEFAULT_TEXT.ADD_TO_CART;
        productCTA.classList.add(ProductTemplate.CSS_CLASSES.ACTIVE);
      }
    } else {
      // No size options, so product is available
      productCTA.textContent = ProductTemplate.DEFAULT_TEXT.ADD_TO_CART;
      productCTA.classList.add(ProductTemplate.CSS_CLASSES.ACTIVE);
    }
  }

  showSizeError() {
    const sizeError = this.cachedElements.sizeError;
    if (sizeError) {
      sizeError.classList.remove(ProductTemplate.CSS_CLASSES.HIDDEN);
    }
  }

  hideSizeError() {
    const sizeError = this.cachedElements.sizeError;
    if (sizeError) {
      sizeError.classList.add(ProductTemplate.CSS_CLASSES.HIDDEN);
    }
  }

  // ============================================================================
  // ADD TO CART FUNCTIONALITY - Shopping cart integration
  // ============================================================================

  /**
   * Sets up listener for add to cart button clicks
   * Handles both regular products and gift cards
   */
  setupAddToCartListener() {
    this.addEventListener('click', (e) => {
      if (
        e.target.classList.contains(ProductTemplate.CSS_CLASSES.PRODUCT_CTA)
      ) {
        this.handleAddToCartClick(e.target);
      }
    });
  }

  handleAddToCartClick(target) {
    const sizeOption = document.querySelector('.product__option-size');
    const hasHiddenSize = sizeOption?.classList.contains(
      ProductTemplate.CSS_CLASSES.HIDDEN
    );

    if (hasHiddenSize) {
      this.showSizeOptions();
    }

    if (target.classList.contains(ProductTemplate.CSS_CLASSES.ACTIVE)) {
      this.addToCart();
    }
  }

  showSizeOptions() {
    const sizeOption = document.querySelector('.product__option-size');
    const optionTitles = document.querySelectorAll(
      '.product-modal__option-title'
    );

    if (sizeOption) {
      sizeOption.classList.remove(ProductTemplate.CSS_CLASSES.HIDDEN);
    }

    optionTitles.forEach((title) => {
      title.classList.remove(ProductTemplate.CSS_CLASSES.HIDDEN);
    });
  }

  async addToCart() {
    try {
      const selectedVariant = this.getSelectedVariant();
      if (!selectedVariant) {
        throw new Error('No variant selected');
      }

      const formData = this.buildCartFormData(selectedVariant);

      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      this.handleSuccessfulAddToCart();
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.handleAddToCartError(error);
    }
  }

  buildCartFormData(selectedVariant) {
    const variantId = this.decodeId(selectedVariant.node.id);

    const formData = {
      items: [
        {
          id: variantId,
          quantity: 1,
        },
      ],
    };

    if (this.productData.isGiftCard && this.enableGiftCardRecipient) {
      const giftCardData = this.getGiftCardData();
      if (giftCardData) {
        formData.items[0].properties = giftCardData;
      }
    }

    return formData;
  }

  getGiftCardData() {
    const sendAsGift = document.getElementById(
      'send-as-gift-checkbox'
    )?.checked;

    if (!sendAsGift) return null;

    const recipientEmail = document.getElementById('recipient-email')?.value;
    const recipientName = document.getElementById('recipient-name')?.value;
    const message = document.getElementById('gift-message')?.value;

    if (!recipientEmail) {
      alert('Recipient email is required for gift cards.');
      return null;
    }

    return {
      'Recipient email': recipientEmail,
      'Recipient name': recipientName,
      Message: message,
      __shopify_send_gift_card_to_recipient: 'true',
    };
  }

  handleSuccessfulAddToCart() {
    if (window.Shopify?.customerPrivacy?.marketingAllowed()) {
      window.amzn?.('trackEvent', 'AddToShoppingCart');
    }
  }

  handleAddToCartError(error) {
    console.error('Failed to add product to cart:', error);
    // Could add user-facing error handling here
  }

  /**
   * Attempts to select the correct variant in Klaviyo's BIS iframe
   * @param {number} targetVariantId - The variant ID to select
   */
  selectVariantInKlaviyoIframe(targetVariantId) {
    try {
      const iframe = document.getElementById('klaviyo-bis-iframe');
      if (!iframe) return;

      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
      if (!iframeDoc) return;

      const variantSelect = iframeDoc.getElementById('variants');
      if (!variantSelect) return;

      // Find the option with the matching variant ID
      const options = variantSelect.querySelectorAll('option');
      for (let option of options) {
        if (option.value === targetVariantId.toString()) {
          variantSelect.value = targetVariantId.toString();
          // Trigger change event to ensure Klaviyo recognizes the selection
          variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
          break;
        }
      }
    } catch (error) {
      console.error('Error selecting variant in Klaviyo iframe:', error);
    }
  }

  /**
   * Opens a general Klaviyo BIS modal for the current product
   * Allows users to select any unavailable variant from a dropdown
   */
  openGeneralKlaviyoBISModal() {
    // Guard: remove any existing modal before opening a new one
    this.closeGeneralModal();

    const productId = this.decodeId(this.productData.id);
    const unavailableVariants = this.getUnavailableVariants();

    // Build variant options for the dropdown
    let variantOptions = '';
    unavailableVariants.forEach((variant) => {
      const variantId = this.decodeId(variant.node.id);
      variantOptions += `<option value='${variantId}'>${variant.node.title}</option>`;
    });

    // Create a general Klaviyo BIS modal for the product
    const klaviyoBisHTML = `
      <div class="general-klaviyo-modal" product-id='${productId}'>
        <div class='klaviyo-bis-modal-overlay fixed inset-0 overflow-y-auto flex items-center justify-center' aria-labelledby='modal-title' role='dialog' aria-modal='true' style='background-color: rgba(0, 0, 0, 0.5); z-index: 999999;'>
          <div id='klaviyo-bis-modal' class='relative'>
            <div class='bg-white relative' style='padding: 32px 18px 40px 18px; max-width: 460px;' id='container'>
              <button type='button' class='close klaviyo-bis-close' data-dismiss='modal' style='position: absolute; top: 10px; right: 15px; font-size: 24px; background: none; border: none; cursor: pointer;'>×</button>
              <h3 class='modal-title'>${this.productData.title}</h3>
              <p>Select the size you want to be notified about when it's back in stock.</p>
              <form action='#' class='form-horizontal clearfix' id='general-klaviyo-form'>
                <div class='form-group' style='margin-bottom: 15px;'>
                  <div id='variant_select' class='col-sm-12'>
                    <label for='variant-select' style='display: block; margin-bottom: 5px; font-weight: bold;'>Size:</label>
                    <div style='position: relative;'>
                      <select id='variant-select' name='variant' class='form-control input-lg' style='width: 100%; padding: 10px 30px 10px 10px; border: 1px solid #ccc; border-radius: 4px; appearance: none; background-color: white;'>
                        ${variantOptions}
                      </select>
                      <div style='position: absolute; right: 10px; top: 50%; transform: translateY(-50%); pointer-events: none; color: #666;'>
                        <svg width='12' height='12' viewBox='0 0 12 12' fill='currentColor'>
                          <path d='M6 9L2 5h8L6 9z'/>
                        </svg>
                      </div>
                    </div>
                    <input type='hidden' id='product' value='${productId}'>
                  </div>
                </div>
                <div class='form-group' style='margin-bottom: 15px;'>
                  <div class='col-sm-12'>
                    <input required type='email' id='email' placeholder='Email' class='form-control input-lg' value=''>
                  </div>
                  <div id='newsletter_subscription' style='display: none;'>
                    <p>
                      <input type='checkbox' id='subscribe_for_newsletter' checked=''>
                      <label for='subscribe_for_newsletter' style='margin-left:10px;'>Add me to your email list.</label>
                    </p>
                  </div>
                </div>
                <div class='control-group clearfix submit-container'>
                  <button type='submit' class='btn btn-notify btn-success btn-lg col-xs-12 hover:!bg-gold'>Notify me when available</button>
                </div>
                <p class='text-sm'>
                  By clicking "Notify Me" you agree to be brainwashed by Liquid Death marketing through rare
                  (but hilarious) emails and agreeing to our
                  <a class='underline' href='/policies/privacy-policy'>Terms & Conditions</a> /
                  <a class='underline' href='/policies/terms-of-service'>Privacy Policy</a>
                </p>
                <div id='error_message' class='error_message alert alert-danger hidden'></div>
                <div id='completed_message' class='completed_message alert alert-success hidden'>
                  You're in! We'll let you know when it's back. <a href='#' class='klaviyo-bis-close'>Close</a>
                </div>
              </form>
              <p class='small-print'></p>
            </div>
          </div>
        </div>
      </div>
    `;

    // Insert the modal into the page
    document.body.insertAdjacentHTML('beforeend', klaviyoBisHTML);

    // Add event listeners for modal close functionality
    this.setupGeneralModalCloseListeners(null, productId);
  }

  /**
   * Sets up event listeners for the general Klaviyo BIS modal
   * Scoped to the current modal instance to avoid stacking handlers
   */
  setupGeneralModalCloseListeners(variantId, productId) {
    // Get the current modal (the one just inserted, should be last)
    const currentModal = document.querySelector('.general-klaviyo-modal');
    if (!currentModal) return;

    // Close buttons - scoped to current modal
    const closeButtons = currentModal.querySelectorAll('.klaviyo-bis-close');
    closeButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.closeGeneralModal();
      });
    });

    // Close on overlay click - scoped to current modal
    const modalOverlay = currentModal.querySelector(
      '.klaviyo-bis-modal-overlay'
    );
    if (modalOverlay) {
      modalOverlay.addEventListener('click', (e) => {
        if (e.target === modalOverlay) {
          this.closeGeneralModal();
        }
      });
    }

    // Close on Escape key - store handler to prevent stacking
    const escapeHandler = (e) => {
      if (e.key === 'Escape') {
        const existingModal = document.querySelector('.general-klaviyo-modal');
        if (existingModal) {
          this.closeGeneralModal();
          document.removeEventListener('keydown', escapeHandler);
        }
      }
    };
    // Remove any previous escape handler and add new one
    if (this._escapeHandler) {
      document.removeEventListener('keydown', this._escapeHandler);
    }
    this._escapeHandler = escapeHandler;
    document.addEventListener('keydown', escapeHandler);

    // Handle notify button click - scoped to current modal
    const notifyButton = currentModal.querySelector(
      '#general-klaviyo-form .btn-notify'
    );
    if (notifyButton) {
      notifyButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (variantId) {
          // Specific variant modal
          this.handleGeneralKlaviyoFormSubmit(e, variantId, productId);
        } else {
          // "Can't find your size?" modal - get variant from dropdown
          this.handleGeneralKlaviyoFormSubmit(e, null, productId);
        }
      });
    }
  }

  /**
   * Close the general Klaviyo BIS modal
   * Removes ALL modal instances to prevent stacking
   */
  closeGeneralModal() {
    const modalElements = document.querySelectorAll('.general-klaviyo-modal');
    modalElements.forEach((modal) => modal.remove());

    // Clean up escape handler
    if (this._escapeHandler) {
      document.removeEventListener('keydown', this._escapeHandler);
      this._escapeHandler = null;
    }
  }

  /**
   * Handle form submission for the general Klaviyo BIS modal
   */
  async handleGeneralKlaviyoFormSubmit(e, variantId, productId) {
    const form = document.querySelector('#general-klaviyo-form');
    if (!form) return;

    const emailInput = form.querySelector('#email');
    let selectedVariantId = variantId;

    if (!emailInput) {
      console.error('Email input not found');
      return;
    }

    const email = emailInput.value.trim();

    if (!email) {
      const errorMessage = form.querySelector('#error_message');
      if (errorMessage) {
        errorMessage.textContent = 'Please enter your email address';
        errorMessage.classList.remove('hidden');
      }
      return;
    }

    // If no variantId passed, get it from dropdown (for "Can't find your size?" modal)
    if (!variantId) {
      const variantSelect = form.querySelector('#variant-select');
      if (!variantSelect) {
        console.error('Variant select not found');
        return;
      }
      selectedVariantId = variantSelect.value;

      if (!selectedVariantId) {
        const errorMessage = form.querySelector('#error_message');
        if (errorMessage) {
          errorMessage.textContent = 'Please select a size';
          errorMessage.classList.remove('hidden');
        }
        return;
      }
    }

    try {
      const payload = {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                },
              },
            },
            channels: ['EMAIL'],
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: `$shopify:::$default:::${selectedVariantId}`,
              },
            },
          },
        },
      };

      const response = await fetch(
        'https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=Ntc5BJ',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            revision: '2024-06-15',
          },
          body: JSON.stringify(payload),
        }
      );

      if (response.ok) {
        // Show success message
        const successMessage = form.querySelector('#completed_message');
        if (successMessage) {
          successMessage.classList.remove('hidden');
        }
      } else {
        console.error('❌ Klaviyo API call failed:', response.status);
        const errorText = await response.text();
        console.error('Error response:', errorText);
        const errorMessage = form.querySelector('#error_message');
        if (errorMessage) {
          errorMessage.textContent =
            'There was an error submitting your request. Please try again.';
          errorMessage.classList.remove('hidden');
        }
      }
    } catch (error) {
      console.error('Error calling Klaviyo API:', error);
      const errorMessage = form.querySelector('#error_message');
      if (errorMessage) {
        errorMessage.textContent =
          'There was an error submitting your request. Please try again.';
        errorMessage.classList.remove('hidden');
      }
    }
  }

  /**
   * Get all unavailable variants for the current product
   */
  getUnavailableVariants() {
    return (
      this.productData?.variants?.edges?.filter(
        (variant) => !variant.node.availableForSale
      ) || []
    );
  }

  /**
   * Handle click on unavailable variant size option
   * Opens Klaviyo BIS modal for that specific variant
   */
  handleUnavailableVariantClick(variant) {
    // Guard: remove any existing modal before opening a new one
    this.closeGeneralModal();

    const productId = this.decodeId(this.productData.id);
    const variantId = this.decodeId(variant.node.id);

    // Create a specific Klaviyo BIS modal for this variant
    const klaviyoBisHTML = `
      <div class="general-klaviyo-modal" product-id='${productId}'>
        <div class='klaviyo-bis-modal-overlay fixed inset-0 overflow-y-auto flex items-center justify-center' aria-labelledby='modal-title' role='dialog' aria-modal='true' style='background-color: rgba(0, 0, 0, 0.5); z-index: 999999;'>
          <div id='klaviyo-bis-modal' class='relative'>
            <div class='bg-white relative' style='padding: 32px 18px 40px 18px; max-width: 460px;' id='container'>
              <button type='button' class='close klaviyo-bis-close' data-dismiss='modal' style='position: absolute; top: 10px; right: 15px; font-size: 24px; background: none; border: none; cursor: pointer;'>×</button>
              <h3 class='modal-title'>${this.productData.title}</h3>
              <p>Get notified when <strong>${variant.node.title}</strong> is back in stock.</p>
              <form action='#' class='form-horizontal clearfix' id='general-klaviyo-form'>
                <div class='form-group' style='margin-bottom: 15px;'>
                  <div class='col-sm-12'>
                    <input required type='email' id='email' placeholder='Email' class='form-control input-lg' value=''>
                  </div>
                  <div id='newsletter_subscription' style='display: none;'>
                    <p>
                      <input type='checkbox' id='subscribe_for_newsletter' checked=''>
                      <label for='subscribe_for_newsletter' style='margin-left:10px;'>Add me to your email list.</label>
                    </p>
                  </div>
                </div>
                <div class='control-group clearfix submit-container'>
                  <button type='submit' class='btn btn-notify btn-success btn-lg col-xs-12 hover:!bg-gold'>Notify me when available</button>
                </div>
                <p class='text-sm'>
                  By clicking "Notify Me" you agree to be brainwashed by Liquid Death marketing through rare
                  (but hilarious) emails and agreeing to our
                  <a class='underline' href='/policies/privacy-policy'>Terms & Conditions</a> /
                  <a class='underline' href='/policies/terms-of-service'>Privacy Policy</a>
                </p>
                <div id='error_message' class='error_message alert alert-danger hidden'></div>
                <div id='completed_message' class='completed_message alert alert-success hidden'>
                  You're in! We'll let you know when it's back. <a href='#' class='klaviyo-bis-close'>Close</a>
                </div>
              </form>
              <p class='small-print'></p>
            </div>
          </div>
        </div>
      </div>
    `;

    // Insert the modal into the page
    document.body.insertAdjacentHTML('beforeend', klaviyoBisHTML);

    // Add event listeners for modal close functionality
    this.setupGeneralModalCloseListeners(variantId, productId);
  }
}

// ============================================================================
// COMPONENT REGISTRATION - Register the custom element
// ============================================================================

/**
 * Register the ProductTemplate as a custom HTML element
 * Allows usage as <product-template> in HTML
 */
customElements.define('product-template', ProductTemplate);
